import PropTypes from 'prop-types';

import Form from 'react-validation/build/form';

import CustomDatePicker from 'atoms/CustomDatePicker';
import SelectButtonGroup from 'atoms/SelectButtonGroup';
import TextInput from 'atoms/TextInput';
import { generateObjectArray } from 'utils/helpers';
import { required } from 'utils/validations';

import { useState } from 'react';

/**
 *
 * PatientDetails
 *
 */

const PatientDetails = (props) => {
	const {
		nextStep,
		prevStep,
		handleChange,
		handleDateChange,
		data,
		isMobile,
		hidePrev,
	} = props;
	const {
		registrationType,
		name,
		preferredName,
		childName,
		maritalStatus,
		dob,
		sex,
		genderIdentity,
		sexualOrientation,
		pronouns,
	} = data;

	const nameSplit = name.split(' ');
	let initFirstName = nameSplit[0];
	let initMiddleName = '';
	let initLastName = '';
	if (nameSplit.length === 2) {
		initLastName = nameSplit[1];
	} else if (nameSplit.length > 2) {
		initLastName = nameSplit[nameSplit.length - 1];
		initMiddleName = nameSplit.splice(1, nameSplit.length - 2).join(' ');
	}

	const [firstName, setFirstName] = useState(initFirstName);
	const [middleName, setMiddleName] = useState(initMiddleName);
	const [lastName, setLastName] = useState(initLastName);

	const handleNameChange = (event) => {
		if (event.target.name === 'firstName') {
			setFirstName(event.target.value);
		} else if (event.target.name === 'middleName') {
			setMiddleName(event.target.value);
		} else if (event.target.name === 'lastName') {
			setLastName(event.target.value);
		}
	};

	function isFormComplete() {
		if (registrationType === 'child') {
			// The child form collects the guardian/patient full names straight
			// into data (name/childName); the first/last-name state frozen at
			// mount is never updated by its inputs. Gating on lastName would
			// leave a single-word account name permanently stuck (lastName
			// freezes to '' and nothing can change it), so require only what
			// this form actually collects.
			return (
				Boolean(dob) &&
				Boolean(maritalStatus) &&
				Boolean((name || '').trim()) &&
				Boolean((childName || '').trim())
			);
		}
		return (
			Boolean(dob) &&
			Boolean(firstName) &&
			Boolean(lastName) &&
			Boolean(maritalStatus)
		);
	}

	const handleNextStep = () => {
		if (registrationType === 'child') {
			// The child form edits data.name directly; recomposing it from the
			// frozen first/middle/last state would overwrite guardian edits.
			nextStep();
			return;
		}

		let nameValue = `${firstName || ''} ${middleName || ''} ${lastName || ''}`;
		nameValue = nameValue.replace(/\s+/g, ' '); //remove extra spaces

		// Sync the composed name into the parent's formData, but ALSO pass the
		// updated formData to nextStep: the parent reads formData synchronously
		// to build the submit payload, before this setState flushes.
		handleChange('patientDetails')({ currentTarget: { name: 'name', value: nameValue } });
		nextStep({ ...data, name: nameValue });
	};

	return (
		<Form>
			{registrationType !== 'child' && (
				<>
					<div className="form-row">
						<div className="col-md-6 form-group mb-0">
							<TextInput
								label="PATIENT'S FIRST NAME*"
								id="firstName"
								value={firstName}
								placeholder="John"
								handleInput={handleNameChange}
								validations={[required]}
							/>
						</div>
						<div className="col-md-6 form-group mb-0">
							<TextInput
								label="PATIENT'S LAST NAME*"
								id="lastName"
								value={lastName}
								placeholder="Doe"
								handleInput={handleNameChange}
								validations={[required]}
							/>
						</div>
					</div>
					<div className="form-row mb-0">
						<div className="col-md-6 form-group mb-0">
							<TextInput
								label="PATIENT'S MIDDLE NAME"
								id="middleName"
								value={middleName}
								placeholder="Robert"
								handleInput={handleNameChange}
							/>
						</div>
						<div className="col-md-6 form-group mb-0">
							<TextInput
								label="PATIENT'S PREFERRED NAME"
								id="preferredName"
								value={preferredName}
								placeholder="Luke"
								handleInput={handleChange('patientDetails')}
							/>
						</div>
					</div>
				</>
			)}
			{registrationType === 'child' && (
				<div>
					<div className="form-group mb-0">
						<TextInput
							label="GUARDIAN'S FULL NAME*"
							id="name"
							value={name}
							placeholder="John Lucan Doe"
							handleInput={handleChange('patientDetails')}
							validations={[required]}
						/>
					</div>
					<div className="form-group">
						<TextInput
							label="PATIENT'S FULL NAME*"
							id="childName"
							value={childName || ''}
							placeholder="John Lucan Doe"
							handleInput={handleChange('patientDetails')}
							validations={[required]}
						/>
					</div>
				</div>
			)}
			<div className="form-group">
				<label htmlFor="maritalStatus" className="form-font">
					MARITAL STATUS*
				</label>
				<SelectButtonGroup
					id="maritalStatus"
					value={maritalStatus}
					buttons={generateObjectArray([
						'Never Married',
						'Married',
						'Divorced',
						'Separated',
						'Widowed',
						'Partnered',
					])}
					className={`${isMobile && 'row mx-0'}`}
					buttonClass="font-14"
					handleSelect={handleChange('patientDetails')}
				/>
			</div>

			<div className="form-row">
				<CustomDatePicker
					className="col-md-6"
					label={<span className="form-group">DATE OF BIRTH*</span>}
					id="dob"
					selected={dob}
					handleDateChange={handleDateChange('patientDetails', 'dob')}
					maxDate={new Date()}
					placeholderText="Select Date of Birth"
				/>
				<div className="col-md-6 form-group">
					<label htmlFor="sex" className="form-font">
						SEX ASSSIGNED AT BIRTH*
					</label>
					<SelectButtonGroup
						id="sex"
						value={sex}
						buttons={generateObjectArray(['Male', 'Female', 'Other'])}
						handleSelect={handleChange('patientDetails')}
						validations={[required]}
						className={`${isMobile && 'row mx-0'}`}
					/>
				</div>
			</div>
			<div className="form-row mb-0">
				<div className="col-md-6 form-group mb-0">
					<TextInput
						label="GENDER IDENTITY"
						id="genderIdentity"
						value={genderIdentity}
						placeholder=""
						handleInput={handleChange('patientDetails')}
					/>
				</div>
				<div className="col-md-6 form-group mb-0">
					<TextInput
						label="SEXUAL ORIENTATION"
						id="sexualOrientation"
						value={sexualOrientation}
						placeholder=""
						handleInput={handleChange('patientDetails')}
					/>
				</div>
			</div>
			<div className="col-14 form-group">
				<TextInput
					label="PRONOUNS"
					id="pronouns"
					value={pronouns}
					placeholder=""
					handleInput={handleChange('patientDetails')}
				/>
			</div>
			<div className="d-flex mb-3" />
			<div className="d-flex mt-3">
				{/* Spacer kept when hidden (track-typed link) so NEXT stays aligned */}
				<div className="col-6 pl-0">
					{!hidePrev && (
						<button
							type="button"
							className="btn btn-white link-color border btn-lg btn-block font-weight-bold d-flex justify-content-start font-15"
							onClick={prevStep}
						>
							<div className="col-4 d-flex justify-content-start align-self-center">
								<img className="fas fa-arrow-left" alt="arrow-left" />
							</div>
							<div className="col-4 d-flex justify-content-center">
								<span>PREV</span>
							</div>
						</button>
					)}
				</div>
				<div className="col-6 p-0 d-flex">
					<button
						type="button"
						className="btn btn-primary btn-color btn-lg btn-block font-weight-bold font-15"
						onClick={handleNextStep}
						disabled={!isFormComplete()}
					>
						NEXT
					</button>
				</div>
			</div>
		</Form>
	);
};

PatientDetails.propTypes = {
	nextStep: PropTypes.func.isRequired,
	prevStep: PropTypes.func,
	handleChange: PropTypes.func.isRequired,
	handleDateChange: PropTypes.func.isRequired,
	handleNumberChange: PropTypes.func.isRequired,
	data: PropTypes.object.isRequired,
	hidePrev: PropTypes.bool,
};

export default PatientDetails;
